import { InputMode } from 'src/types'

enum IntegratorType {
  Visa = 'VISA',
  Svb = 'SVB',
  Mastercard = 'MASTERCARD',
  Amex = 'AMEX',
  Comdata = 'COMDATA',
  Dataflex = 'DATAFLEX',
}

export enum IssuerIntegratorSchemaFieldType {
  String = 'string',
  Int = 'int',
  Last4 = 'last4',
  Cardnumber15 = 'cardnumber15',
  Cardnumber16 = 'cardnumber16',
  Expirationdate = 'expirationdate',
}

interface IssuerIntegratorSchemaFieldListResponse {
  name: string
  label: string
  description?: string
  type: IssuerIntegratorSchemaFieldType
  required: boolean
}

export class IssuerIntegratorSchemaFieldListModel {
  public name: string
  public label: string
  public description?: string
  public type: IssuerIntegratorSchemaFieldType
  public required: boolean

  public constructor(data: IssuerIntegratorSchemaFieldListResponse) {
    this.name = data.name
    this.label = data.label
    this.description = data.description
    this.type = data.type
    this.required = data.required
  }

  public get inputMode(): InputMode | undefined {
    switch (this.type) {
      case IssuerIntegratorSchemaFieldType.Expirationdate:
      case IssuerIntegratorSchemaFieldType.Cardnumber15:
      case IssuerIntegratorSchemaFieldType.Cardnumber16:
      case IssuerIntegratorSchemaFieldType.Int:
      case IssuerIntegratorSchemaFieldType.Last4: {
        return 'decimal'
      }
      default: {
        return undefined
      }
    }
  }

  public get formattingFunction(): (val: string) => string {
    switch (this.type) {
      case IssuerIntegratorSchemaFieldType.Expirationdate: {
        return (val: string): string => {
          const onlyNums = val.replace(/[^\d]/g, '')
          if (onlyNums.length < 3) {
            return onlyNums
          } else if (onlyNums.length === 3) {
            return `${onlyNums.slice(0, 1)}/${onlyNums.slice(1, 3)}`
          } else {
            return `${onlyNums.slice(0, 2)}/${onlyNums.slice(2, Math.min(onlyNums.length, 4))}`
          }
        }
      }
      case IssuerIntegratorSchemaFieldType.Cardnumber15: {
        return (val: string): string => {
          const noSpacesOnlyNums = val.replace(/\s+/g, '').replace(/[^\d]/g, '')
          return noSpacesOnlyNums.length > 10
            ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)} ${noSpacesOnlyNums.slice(10, 15)}`
            : noSpacesOnlyNums.length > 4
              ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)}`
              : noSpacesOnlyNums
        }
      }
      case IssuerIntegratorSchemaFieldType.Cardnumber16: {
        return (val: string): string => {
          const noSpacesOnlyNums = val.replace(/\s+/g, '').replace(/[^\d]/g, '')
          return noSpacesOnlyNums.length > 12
            ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)} ${noSpacesOnlyNums.slice(8, 12)} ${noSpacesOnlyNums.slice(12, 16)}`
            : noSpacesOnlyNums.length > 8
              ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)} ${noSpacesOnlyNums.slice(8, 12)}`
              : noSpacesOnlyNums.length > 4
                ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)}`
                : noSpacesOnlyNums
        }
      }
      default: {
        return (val: string): string => val
      }
    }
  }
}

export interface IssuerIntegratorHelpText {
  message: string
  linkLabel?: string
  linkUrl?: string
}
export interface IssuerIntegratorListResponse {
  id: string
  name: string
  description?: string
  integratorType: IntegratorType
  schema: IssuerIntegratorSchemaFieldListResponse[]
  registrable: boolean
  code: string
  contentType?: string
  url: string
  plasticCardEnabled: boolean
  restrictions?: Record<string, string[]>
  needsCardholderVerification: boolean
  helpText?: IssuerIntegratorHelpText
  recipientScreeningEnabled: boolean
  singleExactPayEnabled: boolean
  physicalTransactionsEnabled: boolean
}

export class IssuerIntegratorListModel {
  public id: string
  public name: string
  public description?: string
  public integratorType: IntegratorType
  public schema: IssuerIntegratorSchemaFieldListModel[]
  public registrable: boolean
  public code: string
  public contentType?: string
  public url?: string
  public plasticCardEnabled: boolean
  public restrictions?: Record<string, string[]>
  public needsCardholderVerification: boolean
  public helpText?: IssuerIntegratorHelpText
  public recipientScreeningEnabled: boolean
  public singleExactPayEnabled: boolean
  public physicalTransactionsEnabled: boolean

  public constructor(r: IssuerIntegratorListResponse) {
    this.id = r.id
    this.name = r.name
    this.description = r.description
    this.integratorType = r.integratorType
    this.schema = r.schema.map(
      (field) => new IssuerIntegratorSchemaFieldListModel(field),
    )
    this.registrable = r.registrable
    this.code = r.code
    this.contentType = r.contentType
    this.url = r.url
    this.plasticCardEnabled = r.plasticCardEnabled
    this.restrictions = r.restrictions
    this.needsCardholderVerification = r.needsCardholderVerification
    this.helpText = r.helpText
    this.recipientScreeningEnabled = r.recipientScreeningEnabled
    this.singleExactPayEnabled = r.singleExactPayEnabled
    this.physicalTransactionsEnabled = r.physicalTransactionsEnabled
  }
}
